// API base URL - defaults to proxy for local development
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// API utility functions
export class ApiService {
  static async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}/api${endpoint}`;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    const token = localStorage.getItem('qdn_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);
      
      // Handle different response types
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Special handling for rate limiting
        if (response.status === 429) {
          const retryAfter = data?.retry_after || response.headers.get('Retry-After');
          const error = new Error(data?.error || 'Too many requests. Please slow down.');
          error.status = 429;
          error.retryAfter = retryAfter;
          throw error;
        }
        
        const error = new Error(data?.error || data || 'Network response was not ok');
        error.status = response.status;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  static get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  static post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  static put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  static delete(endpoint, options = {}) {
    return this.request(endpoint, { method: 'DELETE', ...options });
  }
}

// Auth API functions
export const authApi = {
  register: (userData) => ApiService.post('/auth/register', userData),
  login: (credentials) => ApiService.post('/auth/login', credentials),
  getCurrentUser: () => ApiService.get('/auth/me'),
  verifyEmail: (token) => ApiService.post('/auth/verify-email', { token }),
  logout: () => {
    // Stateless logout - just remove tokens
    localStorage.removeItem('qdn_access_token');
    localStorage.removeItem('qdn_refresh_token');
    return Promise.resolve();
  },
};

// User API functions
export const userApi = {
  getUserProfile: (userId) => ApiService.get(`/users/${userId}`),
  updateProfile: (profileData) => ApiService.put('/users/profile', profileData),
  deleteAccount: () => ApiService.delete('/users/me'),
};

// Content API functions 
export const contentApi = {
  getContent: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return ApiService.get(`/content${query ? `?${query}` : ''}`);
  },
  getContentById: (id) => ApiService.get(`/content/${id}`),
  createContent: (contentData) => ApiService.post('/content', contentData),
  updateContent: (id, contentData) => ApiService.put(`/content/${id}`, contentData),
  deleteContent: (id) => ApiService.delete(`/content/${id}`),
};

// Activity API functions
export const activityApi = {
  getActivities: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return ApiService.get(`/activity${query ? `?${query}` : ''}`);
  },
  getActivityById: (id) => ApiService.get(`/activity/${id}`),
  createActivity: (activityData) => ApiService.post('/activity', activityData),
  updateActivity: (id, activityData) => ApiService.put(`/activity/${id}`, activityData),
  deleteActivity: (id) => ApiService.delete(`/activity/${id}`),
  lockActivity: (id, locked) => ApiService.post(`/activity/${id}/lock`, { locked }),
  
  // Reactions
  reactToActivity: (id, value) => ApiService.post(`/activity/${id}/react`, { value }),
  
  // Comments
  createComment: (activityId, commentData) => ApiService.post(`/activity/${activityId}/comments`, commentData),
  updateComment: (activityId, commentId, commentData) => ApiService.put(`/activity/${activityId}/comments/${commentId}`, commentData),
  deleteComment: (activityId, commentId) => ApiService.delete(`/activity/${activityId}/comments/${commentId}`),
};